import { createContext, useContext } from 'react'
import { Box } from '@mui/material'
import { Outlet, useNavigate, useParams } from 'react-router-dom'

import { ResizableSidebarLayout } from '@/pages/User/components/ResizableSidebarLayout'

import { getDataCatalogTablePath } from './catalogMockData'
import { CatalogMain } from './components/CatalogMain'
import { CatalogSidebar } from './components/CatalogSidebar'
import {
	CatalogTableGroup,
	useCatalogFilters,
} from './hooks/useCatalogFilters'

type CatalogOutletContext = {
	groups: CatalogTableGroup[]
	onSelectTable: (tableId: string) => void
	selectedTableId: string | null
	tableCount: number
}

const CatalogContext = createContext<CatalogOutletContext | null>(null)

export const useCatalogOutlet = () => {
	const context = useContext(CatalogContext)
	if (!context) {
		throw new Error('useCatalogOutlet must be used within DataCatalogLayout')
	}
	return context
}

export const DataCatalogLayout = () => {
	const { tableId } = useParams<{ tableId?: string }>()
	const navigate = useNavigate()
	const {
		search,
		setSearch,
		domains,
		systems,
		selectedDomainIds,
		selectedSystemIds,
		toggleDomain,
		toggleSystem,
		filteredTables,
		tablesByDomain,
	} = useCatalogFilters()

	const handleSelectTable = (id: string) => {
		navigate(getDataCatalogTablePath(id))
	}

	const outletValue: CatalogOutletContext = {
		groups: tablesByDomain,
		tableCount: filteredTables.length,
		selectedTableId: tableId ?? null,
		onSelectTable: handleSelectTable,
	}

	return (
		<CatalogContext.Provider value={outletValue}>
			<Box
				data-testid="data-catalog-page"
				sx={{
					display: 'flex',
					height: '100%',
					minHeight: 0,
					overflow: 'hidden',
					bgcolor: '#fff',
				}}
			>
				<ResizableSidebarLayout
					defaultWidth={280}
					localStorageKey="mdd-sidebar-width-data-catalog"
					content={
						<Box sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
							<Outlet />
						</Box>
					}
				>
					<CatalogSidebar
						search={search}
						setSearch={setSearch}
						domains={domains}
						systems={systems}
						selectedDomainIds={selectedDomainIds}
						selectedSystemIds={selectedSystemIds}
						selectedTableId={tableId ?? null}
						filteredTables={filteredTables}
						onToggleDomain={toggleDomain}
						onToggleSystem={toggleSystem}
						onSelectTable={handleSelectTable}
					/>
				</ResizableSidebarLayout>
			</Box>
		</CatalogContext.Provider>
	)
}

export const DataCatalogIndex = () => {
	const { groups, tableCount, selectedTableId, onSelectTable } =
		useCatalogOutlet()

	return (
		<CatalogMain
			groups={groups}
			tableCount={tableCount}
			selectedTableId={selectedTableId}
			onSelectTable={onSelectTable}
		/>
	)
}
